<template>
  <div class="chat-layout">
    <div class="chapter-list">
      <h3>全部章节</h3>
      <ul>
        <li
          v-for="node in playedNodes"
          :key="node.id"
          :class="['chapter-item', { active: node.id === node_id }]"
        >
          <el-button type="text" @click="retryNode(node.id)">
            {{ node.title }}
          </el-button>
        </li>
      </ul>
    </div>
    <div class="chat-main">
      <div class="chat-container">
        <!-- 进度条 -->
        <div class="progress-container" v-if="currentNode && currentNode.process && currentNode.process.length > 0">
          <el-card
            shadow="hover"
            class="progress-card"
            v-for="(processConfig, index) in currentNode.process.filter(p => p && p.name)"
            :key="index"
          >
            <div class="progress-header">
              <span class="progress-title">{{ processConfig.name || '游戏进度' }}</span>
              <span class="progress-text">{{ processValues[processConfig.name] || 0 }} / {{ processConfig.end_process }}</span>
            </div>
            <el-progress
              :percentage="calculateProgressPercentage(processConfig)"
              :stroke-width="20"
              :show-text="false"
              class="progress-bar"
            />
            <div class="progress-markers">
              <div class="marker start-marker">
                <span class="marker-label">开始</span>
                <span class="marker-value">{{ processConfig.start_process }}</span>
              </div>
              <div class="marker current-marker">
                <span class="marker-label">当前</span>
                <span class="marker-value">{{ processValues[processConfig.name] || 0 }}</span>
              </div>
              <div class="marker end-marker">
                <span class="marker-label">目标</span>
                <span class="marker-value">{{ processConfig.end_process }}</span>
              </div>
            </div>
          </el-card>
        </div>
        <div class="chat-history" ref="chatHistory">
          <div
            v-for="(msg, index) in messages"
            :key="index"
            :class="['chat-message', msg.sender.role_type=='NPC' || msg.sender.role_type=='NARRATOR' ? 'left' : 'right']"
          >
            <img :src="msg.sender.avatar" alt="avatar" class="avatar"  v-if="msg.sender.role_type !== 'NARRATOR'" />
            <div class="bubble">
              <span class="sender" v-if="msg.sender.role_type !== 'NARRATOR'">{{ msg.sender.name }}</span>
              <span v-if="msg.sender.role_type !== 'NARRATOR'">: </span>
              <span class="content" v-html="msg.content.replace(/\n/g, '<br>')"></span>
              <el-button 
                size="mini" 
                type="text" 
                icon="el-icon-video-play" 
                @click="playVoiceForMessage(msg.content)"
                class="play-button"
                :disabled="msg.streaming"
                v-if="msg.sender.role_type !== 'USER'"
              ></el-button>
            </div>
          </div>
        </div>

        <div class="chat-input">
          <el-input
            type="textarea"
            v-model="inChat"
            placeholder="请输入消息..."
            :disabled="loading"
            rows="2"
            @keydown.native="handleKeyDown"
          />
          <div v-if="loading" class="loading-hint">
            正在请求 Chat API...
          </div>
          <div class="input-controls">
            <div class="rag-switch">
              <span class="rag-label">使用RAG：</span>
              <el-switch v-model="useRag"></el-switch>
            </div>
            <div class="rag-switch">
              <span class="rag-label">JSON格式：</span>
              <el-switch v-model="useJsonFormat"></el-switch>
            </div>
            <el-button
              type="primary"
              v-if="!isFinished"
              @click="sendMessage"
              :disabled="loading"
            >
              发送
            </el-button>
            <el-button
              type="success"
              v-else
              @click="goToNextNode"
            >
              下一章
            </el-button>
            <el-button
              :type="recording ? 'danger' : 'warning'"
              @mousedown.native="onVoiceHoldStart"
              @mouseup.native="onVoiceHoldEnd"
              @touchstart.native.prevent="onVoiceHoldStart"
              @touchend.native.prevent="onVoiceHoldEnd"
            >
              {{ recording ? '松开结束' : '按住说话' }}
            </el-button>
          </div>
          <div class="asr-hint" v-if="asrText">{{ asrText }}</div>
        </div>
        <div class="action-buttons">
          <el-button type="primary" @click="deleteCurrentRecord">删除当前存档</el-button>
          <el-button type="danger" @click="deleteCurrentNodeMessages">开始/重新开始当前章节</el-button>
        </div>
      </div>
    </div>
    <div class="sidebar">
      <div class="user-stats">
        <!-- 好感度 -->
        <el-card shadow="hover" class="stats-card clickable" @click.native="showAffectionDetails">
          <div slot="header" class="stats-header">
            <i class="el-icon-star-on"></i>
            <span>好感度</span>
          </div>
          <div v-if="recordDetail && recordDetail.affections && recordDetail.affections.length > 0">
            <div
              v-for="affection in recordDetail.affections"
              :key="affection.role_id"
              class="affection-item"
            >
              <span class="character-name">{{ getCharacterName(affection.role_id) }}</span>
              <span class="affection-value">{{ affection.affection_value }}</span>
            </div>
          </div>
          <div v-else class="empty-state">暂无好感度数据</div>
        </el-card>

        <!-- 背包 -->
        <el-card shadow="hover" class="stats-card clickable" @click.native="showItemsDetails">
          <div slot="header" class="stats-header">
            <i class="el-icon-shopping-bag-1"></i>
            <span>背包</span>
          </div>
          <div v-if="recordDetail && recordDetail.statistics">
            <div class="stat-item">
              <span>道具种类：</span>
              <span class="stat-value">{{ recordDetail.statistics.unique_items }}</span>
            </div>
            <div class="stat-item">
              <span>道具总数：</span>
              <span class="stat-value">{{ recordDetail.statistics.total_items }}</span>
            </div>
          </div>
          <div v-else class="empty-state">暂无背包数据</div>
        </el-card>

        <!-- 成就 -->
        <el-card shadow="hover" class="stats-card clickable" @click.native="showAchievementsDetails">
          <div slot="header" class="stats-header">
            <i class="el-icon-trophy"></i>
            <span>成就</span>
          </div>
          <div v-if="recordDetail && recordDetail.statistics">
            <div class="stat-item">
              <span>成就数量：</span>
              <span class="stat-value">{{ recordDetail.statistics.total_achievements }}</span>
            </div>
          </div>
          <div v-else class="empty-state">暂无成就数据</div>
        </el-card>

        <!-- 事件 -->
        <el-card shadow="hover" class="stats-card clickable" @click.native="showEventsDetails">
          <div slot="header" class="stats-header">
            <i class="el-icon-bell"></i>
            <span>事件</span>
          </div>
          <div v-if="recordDetail && recordDetail.statistics">
            <div class="stat-item">
              <span>事件数量：</span>
              <span class="stat-value">{{ recordDetail.statistics.total_events }}</span>
            </div>
          </div>
          <div v-else class="empty-state">暂无事件数据</div>
        </el-card>

        <!-- 彩蛋 -->
        <el-card shadow="hover" class="stats-card clickable" @click.native="showEasterEggsDetails">
          <div slot="header" class="stats-header">
            <i class="el-icon-magic-stick"></i>
            <span>彩蛋</span>
          </div>
          <div v-if="recordDetail && recordDetail.statistics">
            <div class="stat-item">
              <span>彩蛋数量：</span>
              <span class="stat-value">{{ recordDetail.statistics.total_easter_eggs }}</span>
            </div>
          </div>
          <div v-else class="empty-state">暂无彩蛋数据</div>
        </el-card>
      </div>
    </div>

    <!-- 详情弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <!-- 好感度详情 -->
      <div v-if="dialogType === 'affection'">
        <div v-if="recordDetail && recordDetail.affections && recordDetail.affections.length > 0">
          <div
            v-for="affection in recordDetail.affections"
            :key="affection.role_id"
            class="detail-item"
          >
            <div class="detail-header">
              <img v-if="affection.role_info" :src="affection.role_info.avatar_url" class="detail-avatar" />
              <div class="detail-info">
                <h4>{{ affection.role_info ? affection.role_info.name : getCharacterName(affection.role_id) }}</h4>
                <div class="affection-detail">
                  <span class="label">好感度：</span>
                  <span class="value affection-value">{{ affection.affection_value }}</span>
                </div>
                <div class="time-info">
                  <span class="label">更新时间：</span>
                  <span class="value">{{ formatTime(affection.update_time) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-detail">暂无好感度数据</div>
      </div>

      <!-- 背包详情 -->
      <div v-if="dialogType === 'items'">
        <div v-if="recordDetail && recordDetail.items && recordDetail.items.length > 0">
          <div
            v-for="item in recordDetail.items"
            :key="item.id"
            class="detail-item"
          >
            <div class="detail-content">
              <h4>{{ item.item_name }}</h4>
              <div class="item-detail">
                <span class="label">数量：</span>
                <span class="value item-quantity">{{ item.quantity }}</span>
              </div>
              <div v-if="item.item_desc" class="item-detail">
                <span class="label">描述：</span>
                <span class="value">{{ item.item_desc }}</span>
              </div>
              <div v-if="item.obtain_node_info" class="item-detail">
                <span class="label">获得章节：</span>
                <span class="value">{{ item.obtain_node_info.title }}</span>
              </div>
              <div class="time-info">
                <span class="label">获得时间：</span>
                <span class="value">{{ formatTime(item.create_time) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-detail">暂无道具数据</div>
      </div>

      <!-- 成就详情 -->
      <div v-if="dialogType === 'achievements'">
        <div v-if="recordDetail && recordDetail.achievements && recordDetail.achievements.length > 0">
          <div
            v-for="achievement in recordDetail.achievements"
            :key="achievement.id"
            class="detail-item"
          >
            <div class="detail-content">
              <h4>{{ achievement.achievement_name }}</h4>
              <div v-if="achievement.achievement_desc" class="achievement-detail">
                <span class="label">描述：</span>
                <span class="value">{{ achievement.achievement_desc }}</span>
              </div>
              <div v-if="achievement.node_info" class="achievement-detail">
                <span class="label">获得章节：</span>
                <span class="value">{{ achievement.node_info.title }}</span>
              </div>
              <div class="time-info">
                <span class="label">获得时间：</span>
                <span class="value">{{ formatTime(achievement.create_time) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-detail">暂无成就数据</div>
      </div>

      <!-- 彩蛋详情 -->
      <div v-if="dialogType === 'easter_eggs'">
        <div v-if="recordDetail && recordDetail.easter_eggs && recordDetail.easter_eggs.length > 0">
          <div
            v-for="egg in recordDetail.easter_eggs"
            :key="egg.id"
            class="detail-item"
          >
            <div class="detail-content">
              <h4>{{ egg.egg_name }}</h4>
              <div v-if="egg.egg_desc" class="egg-detail">
                <span class="label">描述：</span>
                <span class="value">{{ egg.egg_desc }}</span>
              </div>
              <div v-if="egg.node_info" class="egg-detail">
                <span class="label">发现章节：</span>
                <span class="value">{{ egg.node_info.title }}</span>
              </div>
              <div class="time-info">
                <span class="label">发现时间：</span>
                <span class="value">{{ formatTime(egg.create_time) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-detail">暂无彩蛋数据</div>
      </div>

      <!-- 事件详情 -->
      <div v-if="dialogType === 'events'">
        <div v-if="recordDetail && recordDetail.events && recordDetail.events.length > 0">
          <div
            v-for="event in recordDetail.events"
            :key="event.id"
            class="detail-item"
          >
            <div class="detail-content">
              <h4>{{ event.achievement_name }}</h4>
              <div v-if="event.achievement_desc" class="event-detail">
                <span class="label">描述：</span>
                <span class="value">{{ event.achievement_desc }}</span>
              </div>
              <div v-if="event.node_info" class="event-detail">
                <span class="label">触发章节：</span>
                <span class="value">{{ event.node_info.title }}</span>
              </div>
              <div class="time-info">
                <span class="label">触发时间：</span>
                <span class="value">{{ formatTime(event.create_time) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-detail">暂无事件数据</div>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts">
// 语音相关：浏览器原生 MediaRecorder/Web Audio API
import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import { getChatHistory, sendChat, Sender } from '@/api/chat'
import {delUserRecord, getUserRecord, getRecordDetail, RecordDetail, Record} from "@/api/record";
import {GameNode, retryPlayedNode, getNode} from "@/api/node";
import {getRoles, Role} from "@/api/role";
import {getUserPlayNodes} from "@/api/node";
import {deleteRecordNode} from "@/api/node";
import {Game} from "@/api/game";
import {getToken} from "@/utils/auth";
import axios from 'axios';
import {getTalk} from "@/api/goserver";


interface Message {
  sender: Sender
  content: string
  streaming?: boolean // 是否流式气泡，未结束时为true
}

@Component
export default class ChatSock extends Vue {
  @Prop({ required: true }) readonly game!: Game
  inChat = ''
  messages: Message[] = []
  record_id = -1
  node_id = -1
  currentNode: GameNode|null = null
  currentRecord: Record|null = null
  socket: WebSocket|undefined = undefined
  loading = false

  playedNodes: GameNode[] = []
  isFinished = false
  currentRole: Sender|null = null
  currentText = ""

  user: Sender = {
    name: '你',
    avatar: 'https://i.pravatar.cc/40?img=1',
    role_id: 2,
    role_type: 'npc',
  }

  // 语音相关
  recording = false
  asrText = ''
  mediaRecorder: MediaRecorder | null = null
  audioStream: MediaStream | null = null
  // 用于收集录音分片
  recordChunks: Blob[] = []
  
  // 语音缓存 Map: 文本内容 -> base64音频数据
  voiceCache: Map<string, string> = new Map()

  // 存档详细信息
  recordDetail: RecordDetail | null = null

  // 角色列表
  roles: Role[] = []

  // 弹窗相关
  dialogVisible = false
  dialogType = ''
  dialogTitle = ''

  // 存储每个进度条的当前值
  processValues: { [key: string]: number } = {}

  // RAG开关状态
  useRag = false

  // JSON格式开关状态
  useJsonFormat = false

  // 计算单个进度条的百分比
  calculateProgressPercentage(processConfig: any): number {
    const currentValue = this.processValues[processConfig.name] || 0
    const { start_process, end_process } = processConfig

    if (end_process <= start_process) return 0

    const progress = Math.max(0, Math.min(100,
      ((currentValue - start_process) / (end_process - start_process)) * 100
    ))
    return Math.round(progress)
  }

  @Watch("game", {immediate: true})
  onGameChange(newGame: Game){
    if (newGame && newGame.id) {
      this.loadData()
    }
  }
  mounted() {
    this.loadData() // 仍然可以保留 HTTP 加载历史
    this.setupAudio()
  }
  loadData() {
    this.loadUserRecord().then(()=>{
      this.getRoles().then(()=>{
        let node_id: number | undefined = undefined
        if (this.node_id != -1){
          node_id = this.node_id
        }
        if (node_id == undefined){
          this.messages = []
          return
        }
        this.loadCurrentNode().then(() => {
          this.loadHistory(node_id)
        })
      })
      this.connectSocket()
    })
    this.getPlayedNodes()
  }
  private disconnectSocket() {
    if (this.socket) {
      this.socket.close()
      this.socket = undefined
    }
  }

  private reconnectSocket() {
    this.disconnectSocket()
    this.connectSocket()
  }

  finalizeStreamingMessage() {
    const last = this.messages[this.messages.length - 1]
    if (last && last.streaming) {
      // 将上一条流式气泡定格，防止后续同一角色的新消息覆盖
      last.streaming = false
    }
  }

  connectSocket() {
    // 后端部署在 ws://localhost:5000/chat
    let token = getToken() || ""
    console.log(`token is ${token}, this.record_id ${this.record_id}`)
    if (token != null){
      token = encodeURIComponent(token)
    }
    // 本地开发使用 ws，生产环境使用 wss
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    // 根据环境自动选择后端地址：开发环境用本地，生产环境用线上
    const wsHost = process.env.NODE_ENV === 'development'
      ? '127.0.0.1:8090'
      : 'api.xingyuwanxiang.com'
    // 这里的路径 /ws/chat 对应 Go 服务 main.go 中的路由
    this.socket = new WebSocket(`${protocol}//${wsHost}/ws/chat?token=${token}&record_id=${this.record_id}`)

    // 连接成功
    this.socket.onopen = () => {
      console.log('WebSocket 连接成功')
      // 可选：发送身份/历史等初始化消息
      // this.socket.send(JSON.stringify({type: 'join', token: ...}))
    }

    // 收到消息
    this.socket.onmessage = (event) => {
      console.log("socket_data", event.data)
      const data = JSON.parse(event.data)
      // 根据类型分发处理
      if (data.type === 'role') {
        this.finalizeStreamingMessage()
        this.currentRole = data
        this.currentText = ''
      } else if (data.type === 'text') {
        this.currentText += data.content
        this.updateStreamingMessage()
      } else if (data.type === 'finish') {
        this.finalizeStreamingMessage()
        // 清理当前角色和文本状态，防止重复消息
        this.currentRole = null
        this.currentText = ''
        this.isFinished = true
        this.$alert('本章节对话已结束', '提示', { confirmButtonText: '好的' })
      } else if (data.type === 'asr_partial') {
        // 实时语音识别中间结果：显示在输入框上方提示
        this.asrText = data.text || ''
      } else if (data.type === 'asr_final') {
        // 最终识别结果：写入输入框并清空提示
        const t = data.text || ''
        this.asrText = ''
        // 选择：直接发送 or 回填输入框
        this.inChat = t
        // 自动发送：如需自动对话，取消注释
        // this.sendMessage()
        } else if (data.type === 'voice') {
        // 服务端推送 TTS，字段为 base64
        if (data.voice && data.text) {
          // 缓存语音数据，以对话文本内容作为 key
          this.voiceCache.set(data.text, data.voice)
          this.playBase64Audio(data.voice)
        }
      } else if (data.type === 'award') {
        // 处理奖励信号
        this.handleAwardSignal(data)
      } else if (data.type === 'score') {
        // 处理分数更新
        this.handleScoreUpdate(data)
      } else if (data.type === 'multi_accumulated') {
        // 处理多进度条累加更新
        this.handleMultiAccumulated(data)
      }
      this.$nextTick(this.scrollToBottom)
    }

    // 连接关闭/异常
    this.socket.onclose = () => {
      console.log('WebSocket 已关闭，尝试重连...')
      setTimeout(this.connectSocket, 2000)
    }
    this.socket.onerror = (e) => {
      console.log('WebSocket 错误', e)
    }
  }

    // 查找角色对象
  getSenderByName(name: string): Sender {
    // 简单示例，可以更复杂
    if (name === '你') return this.user
    if (name === '旁白') return {
      name: '旁白',
      avatar: '',
      role_id: 0,
      role_type: 'NARRATOR'
    }
    // 可以遍历所有角色，按name查找，否则默认NPC
    return {
      name,
      avatar: '',
      role_id: -1,
      role_type: 'NPC'
    }
  }

  // 用于把流式内容渲染在最后一条气泡
  updateStreamingMessage() {
    if (!this.currentRole || this.isFinished) return
    // 若最后一条为当前角色且未完成，更新，否则新建
    const last = this.messages[this.messages.length - 1]
    if (last && last.sender.name === this.currentRole.name && last.streaming) {
      last.content = this.currentText
    } else {
      this.messages.push({
        sender: this.currentRole,
        content: this.currentText,
        streaming: true
      })
    }
  }

  async deleteCurrentRecord(){
    try{
      await delUserRecord(this.record_id)
      this.$message.info('已删除当前存档，刷新页面重建存档')
    }catch (e){
      this.$message.error('删除失败')
    }
  }
  async goToNextNode() {
    // 假设 playedNodes 按顺序排列
    const idx = this.playedNodes.findIndex(n => n.id === this.node_id)
    if (idx !== -1 && idx < this.playedNodes.length - 1) {
      const nextNode = this.playedNodes[idx + 1]
      if (nextNode.id != null){
        this.node_id = nextNode.id
        this.$nextTick(this.scrollToBottom)
        console.log(`next_id is : ${nextNode.id}`)
      }
    } else {
      this.$message.info("已经是最后一章")
    }
  }

  async deleteCurrentNodeMessages() {
    try {
      const confirmed = await this.$confirm('确定删除当前节点的所有聊天记录吗？', '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      })
      console.log(`开始/重新开始当前章节 ${this.record_id}, ${this.node_id}`)

      // 先清空进度条值和聊天窗口
      this.processValues = {}
      if (this.currentNode && this.currentNode.process && this.currentNode.process.length > 0) {
        for (const processConfig of this.currentNode.process) {
          if (processConfig && processConfig.name) {
            this.$set(this.processValues, processConfig.name, processConfig.start_process || 0)
          }
        }
      }
      this.messages = []

      // 重置完成状态，允许接收新消息
      this.isFinished = false
      this.currentRole = null
      this.currentText = ''

      // 调用删除接口，等待完成
      await deleteRecordNode(this.record_id, this.node_id, true)
      console.log('删除节点记录完成')

      // 删除完成后再发送 WebSocket 消息
      this.socket?.send(JSON.stringify({
        type: 'start_node',
        node_id: this.node_id,
      }))

      this.$message.success('当前节点聊天已开始/重新开始')
    } catch (e) {
      console.error('删除失败:', e)
      this.$message.info('已取消或操作失败')
    }
  }

  async loadUserRecord() {
    this.loading = true;
    try {
      const record = await getUserRecord(this.game.id)
      this.currentRecord = record
      this.record_id = record.record_id
      this.node_id = record.node_id
      console.log(`存档名称： ${record.name}`)
      console.log(`存档 id：${record.record_id}, ${this.record_id}`)

      // 加载存档详细信息
      await this.loadRecordDetail()
    } catch (e) {
      this.$message.error('获取record失败')
    }finally {
      this.loading = false
    }
  }

  async loadRecordDetail() {
    if (this.record_id === -1) {
      return
    }

    try {
      this.recordDetail = await getRecordDetail(this.record_id, this.game.id)
      console.log('存档详细信息:', this.recordDetail)
    } catch (e) {
      this.$message.error('获取存档详细信息失败')
      console.error('获取存档详细信息失败:', e)
    }
  }

  async loadCurrentNode() {
    if (this.node_id === -1) return
    try {
      this.currentNode = await getNode(this.node_id)
      console.log('当前节点信息:', this.currentNode)

      // 初始化进度值
      this.processValues = {}
      if (this.currentNode.process && this.currentNode.process.length > 0) {
        for (const processConfig of this.currentNode.process) {
          if (processConfig && processConfig.name) {
            this.$set(this.processValues, processConfig.name, processConfig.start_process || 0)
          }
        }
      }
    } catch (e) {
      this.$message.error('加载节点信息失败')
      console.error('加载节点信息失败:', e)
    }
  }

  async loadHistory(node_id: number | undefined) {
    try {
      const history = await getChatHistory(this.record_id, node_id)
      let historyMsg = history.res
      this.messages = historyMsg.map(msg => ({
        sender: msg.sender,
        content: msg.content
      }))
      this.$nextTick(this.scrollToBottom)
      for (let i of this.messages){
        console.log(`print message: ${i.content}`)
      }
    } catch (e) {
      // v2.0 版本不需要加载历史记录,静默处理错误
      console.log('加载历史失败 (v2.0 可忽略)', e)
    }
  }

  async getRoles(){
    try{
      this.roles = await getRoles(this.game.id)
      for (let r of this.roles){
        if (r.role_type == "USER"){
          this.user.name = r.name
          this.user.avatar = r.avatar_url
          this.user.role_id = r.id || 0
          this.user.role_type = r.role_type
        }
      }
    } catch (e){
      this.$message.error('加载历史失败1')
    }
  }

  getCharacterName(characterId: number): string {
    const role = this.roles.find(r => r.id === characterId)
    return role ? role.name : `角色${characterId}`
  }

  // 显示好感度详情
  showAffectionDetails() {
    this.dialogType = 'affection'
    this.dialogTitle = '好感度详情'
    this.dialogVisible = true
  }

  // 显示道具详情
  showItemsDetails() {
    this.dialogType = 'items'
    this.dialogTitle = '背包详情'
    this.dialogVisible = true
  }

  // 显示成就详情
  showAchievementsDetails() {
    this.dialogType = 'achievements'
    this.dialogTitle = '成就详情'
    this.dialogVisible = true
  }

  // 显示彩蛋详情
  showEasterEggsDetails() {
    this.dialogType = 'easter_eggs'
    this.dialogTitle = '彩蛋详情'
    this.dialogVisible = true
  }

  // 显示事件详情
  showEventsDetails() {
    this.dialogType = 'events'
    this.dialogTitle = '事件详情'
    this.dialogVisible = true
  }

  // 格式化时间 - 转换为北京时间
  formatTime(timeStr: string): string {
    if (!timeStr) return ''

    // 解析时间字符串，如果没有时区信息则假设为UTC
    let date: Date
    if (timeStr.includes('+') || timeStr.includes('Z')) {
      // 已包含时区信息
      date = new Date(timeStr)
    } else {
      // 假设为UTC时间，添加Z后缀
      date = new Date(timeStr + (timeStr.includes('T') ? 'Z' : 'T00:00:00Z'))
    }

    // 转换为北京时间显示 (UTC+8)
    return date.toLocaleString('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  async sendMessage() {
    const content = this.inChat.trim()
    if (!content) return

    // 重置完成状态，允许新的对话
    this.isFinished = false

    this.messages.push({ sender: this.user, content })
    this.inChat = ''
    this.$nextTick(this.scrollToBottom)

    try {
      this.loading = true
      const payload: any = {
        type: 'chat',
        message: content,
        node_id: this.node_id,
        record_id: this.record_id,
      }

      // 如果RAG开关打开，则添加use_rag字段
      if (this.useRag) {
        payload.use_rag = true
      }

      // 总是发送use_json_format字段，让前端完全控制格式
      payload.use_json_format = this.useJsonFormat

      this.socket?.send(JSON.stringify(payload))
      console.log(`send success...`, payload)
    } catch (e) {
      this.$message.error('系统回复失败')
    }finally {
      this.loading = false
    }
  }

  scrollToBottom() {
    const historyDiv = this.$refs.chatHistory as HTMLElement
    if (historyDiv) {
      historyDiv.scrollTop = historyDiv.scrollHeight
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  async getPlayedNodes() {
    try {
      let res = await getUserPlayNodes(this.game.id)
      this.playedNodes = res || []
    } catch (e) {
      this.$message.error(`加载已玩章节失败`)
    }
  }

  retryNode(nodeId: number|undefined) {
    if (nodeId == undefined){
      this.$message("未选择章节")
      return
    }
    this.$confirm(
      `确定要跳转到章节 "${this.playedNodes.find(n => n.id === nodeId)?.title}" 吗？`,
      '提示',
      {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
      }
    ).then(() => {
      this.node_id = nodeId
      this.loadCurrentNode().then(() => {
        this.loadHistory(nodeId)
      })
      this.socket?.send(JSON.stringify({
        type: 'change_node',
        node_id: this.node_id,
        // 可带token等
      }))
      console.log(`send node_id success...`)
      console.log(`章节 id: ${nodeId}`)
    }).catch(() => {
      console.log('用户取消了重玩章节')
    })
  }

  async callRetryNode(nodeId: number) {
    try {
      await retryPlayedNode(nodeId)
      this.$message.success('已切换章节，请开始重玩')
      await this.loadUserRecord()
      await this.loadHistory(this.node_id)
    } catch (e) {
      this.$message.error('切换章节失败')
    }
  }
  beforeDestroy() {
    if (this.socket) {
      this.socket.close()
      this.socket = undefined
    }
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop()
    }
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(t => t.stop())
      this.audioStream = null
    }
  }

  async setupAudio() {
    try {
      // 仅在浏览器环境使用麦克风
      // 采样率由浏览器控制，服务端可接受 wav/pcm 流；如需 16k，可在后端重采样
      this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // eslint-disable-next-line no-undef
      const options: MediaRecorderOptions = { mimeType: 'audio/webm;codecs=opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType!)) {
        // 兜底
        options.mimeType = 'audio/webm';
      }
      this.mediaRecorder = new MediaRecorder(this.audioStream, options);

      this.mediaRecorder.addEventListener('dataavailable', async (e: BlobEvent) => {
        if (!e.data || e.data.size === 0) return;
        // 收集分片用于“一句话识别”的 HTTP 提交
        this.recordChunks.push(e.data);
        // 同时发送分片给 WS（如果你保留实时识别/其他用途）
        // const buf = await e.data.arrayBuffer();
        // if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        //   this.socket.send(buf);
        // }
      });

      this.mediaRecorder.addEventListener('stop', () => {
        // 结束时告知服务端
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify({ type: 'asr_end' }));
        }
        this.recording = false;
      });
    } catch (err) {
      console.warn('麦克风不可用：', err);
    }
  }

  onVoiceHoldStart() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.$message.warning('连接未就绪，稍后再试');
      return;
    }
    // 清空上次的分片
    this.recordChunks = []
    // 通知服务端开始 ASR，会话参数按需调整
    // this.socket.send(JSON.stringify({ type: 'asr_start', sr: 16000, ch: 1, format: 'webm_opus' }));

    if (this.mediaRecorder && this.mediaRecorder.state !== 'recording') {
      // 若需要更实时，可设置更短的 timeslice（毫秒）
      this.mediaRecorder.start(250);
      this.recording = true;
      this.asrText = '';
    }
  }

  onVoiceHoldEnd() {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    // 将本次录音作为“一句话识别”提交到 HTTP 接口
    this.submitOneShotASR().catch(err => {
      console.warn('ASR submit failed', err)
      this.$message.error('语音识别失败')
    })
  }

  async submitOneShotASR() {
    try {
      if (!this.recordChunks.length) return
      // 先把 webm/opus 分片合并
      const blob = new Blob(this.recordChunks, { type: 'audio/webm;codecs=opus' })
      const webmAb = await blob.arrayBuffer()

      // 用 WebAudio 解码 -> 重采样到 16k -> 转 PCM16 -> 封装 WAV
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext
      const ctx = new AudioCtx()
      // decodeAudioData 可能要求传可变的 ArrayBuffer副本
      const decoded: AudioBuffer = await new Promise((resolve, reject) => {
        const copy = webmAb.slice(0)
        ctx.decodeAudioData(copy as any, resolve, reject)
      })
      await ctx.close()

      // OfflineAudioContext 做高质量重采样到 16k mono
      const durationSec = decoded.duration
      const offline = new (window as any).OfflineAudioContext(1, Math.ceil(durationSec * 16000), 16000)
      const src = offline.createBufferSource()
      src.buffer = decoded
      src.connect(offline.destination)
      src.start(0)
      const rendered: AudioBuffer = await offline.startRendering()

      // 取单声道数据并转为 PCM16
      const f32 = rendered.getChannelData(0)
      const pcm16 = new Int16Array(f32.length)
      for (let i = 0; i < f32.length; i++) {
        let s = Math.max(-1, Math.min(1, f32[i]))
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF
      }

      // 封装成 WAV (PCM16, 16k, mono)
      const wavAb = this.pcmToWav(pcm16.buffer, 16000, 1)

      // 将 WAV 原始字节转为 base64（不要 data: 前缀）
      const b64 = this.arrayBufferToBase64(wavAb)

      // 注意：腾讯 base64 模式要求原始字节 ≤ 600KB
      if (wavAb.byteLength > 600 * 1024) {
        this.$message.warning('录音过长，请缩短后重试（WAV@16k 单声道建议 ≤ 15 秒）')
        return
      }
      // 发送到后端 HTTP 接口（后端会转发到腾讯 ASR）
      const resp = await getTalk(b64)
      const data = resp.res || {}
      if (data && data.text) {
        this.asrText = ''
        this.inChat = data.text   // 回填输入框
        console.log("asr返回信息为：", data.text)
        // 如需自动发送：this.sendMessage()
      } else {
        this.$message.info('未识别到有效文本')
      }
    } finally {
      // 本次分片用完清空
      this.recordChunks = []
    }
  }

  arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    const len = bytes.byteLength
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    // btoa 要求是二进制字符串
    return btoa(binary)
  }

  pcmToWav(pcmAb: ArrayBuffer, sampleRate = 16000, channels = 1): ArrayBuffer {
    const pcmBytes = new Uint8Array(pcmAb)
    const byteRate = sampleRate * channels * 2
    const blockAlign = channels * 2
    const dataSize = pcmBytes.byteLength
    const wavSize = 44 + dataSize
    const buf = new ArrayBuffer(wavSize)
    const dv = new DataView(buf)
    let p = 0
    const wStr = (s: string) => { for (let i=0;i<s.length;i++) dv.setUint8(p++, s.charCodeAt(i)) }
    const w16  = (v: number) => { dv.setUint16(p, v, true); p+=2 }
    const w32  = (v: number) => { dv.setUint32(p, v, true); p+=4 }
    // RIFF/WAVE
    wStr('RIFF'); w32(wavSize-8); wStr('WAVE')
    // fmt chunk
    wStr('fmt '); w32(16); w16(1) // PCM
    w16(channels); w32(sampleRate); w32(byteRate); w16(blockAlign); w16(16)
    // data chunk
    wStr('data'); w32(dataSize)
    new Uint8Array(buf, 44).set(pcmBytes)
    return buf
  }


  // 从服务端收到一条 base64 音频，进行播放（可选，若后端下发 TTS）
  async playBase64Audio(b64: string) {
    try {
      const byteChars = atob(b64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
      const blob = new Blob([bytes.buffer], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.play();
    } catch (e) {
      console.warn('audio play failed', e);
    }
  }

  // 根据对话文本播放语音
  playVoiceForMessage(messageContent: string) {
    // 清理文本内容，去除HTML标签，用于匹配缓存的key
    const cleanText = messageContent.replace(/<[^>]*>/g, '').trim();

    if (this.voiceCache.has(cleanText)) {
      const base64Audio = this.voiceCache.get(cleanText)!;
      this.playBase64Audio(base64Audio);
    } else {
      this.$message.info('语音已被删除');
    }
  }

  // 处理分数更新
  handleScoreUpdate(data: any) {
    if (data.value !== undefined && this.currentRecord) {
      // 更新当前记录的进度分数
      this.currentRecord.current_process = data.value;
      console.log(`分数更新: ${data.value}`);
    }
  }

  // 处理奖励信号
  handleAwardSignal(data: any) {
    const { award_type, name, ...otherFields } = data;

    // 显示获得奖励的通知
    let message = '';
    let notifyType: 'success' | 'info' | 'warning' = 'success';

    switch (award_type) {
      case 'affection':
        message = `好感度奖励：${name}`;
        break;
      case 'item':
        message = `获得道具：${name}`;
        break;
      case 'egg':
        message = `发现彩蛋：${name}`;
        notifyType = 'warning';
        break;
      case 'event':
        message = `触发事件：${name}`;
        notifyType = 'info';
        break;
      case 'achievement':
        message = `获得成就：${name}`;
        break;
      default:
        message = `获得奖励：${name}`;
    }

    console.log('收到奖励信号:', data);

    // 立即更新前端统计数据
    this.updateLocalStatistics(award_type);

    // 显示通知
    this.$notify({
      title: '奖励获得',
      message: message,
      type: notifyType,
      duration: 3000,
      position: 'top-right'
    });

    // 延迟重新加载存档详细信息，确保后端数据已更新
    setTimeout(() => {
      this.loadRecordDetail();
    }, 1000);
  }

  // 立即更新本地统计数据
  updateLocalStatistics(awardType: string) {
    if (!this.recordDetail || !this.recordDetail.statistics) return;

    switch (awardType) {
      case 'achievement':
        this.recordDetail.statistics.total_achievements += 1;
        console.log('本地更新成就数量:', this.recordDetail.statistics.total_achievements);
        break;
      case 'item':
        this.recordDetail.statistics.total_items += 1;
        console.log('本地更新道具数量:', this.recordDetail.statistics.total_items);
        break;
      case 'egg':
        this.recordDetail.statistics.total_easter_eggs += 1;
        console.log('本地更新彩蛋数量:', this.recordDetail.statistics.total_easter_eggs);
        break;
      case 'event':
        this.recordDetail.statistics.total_events += 1;
        console.log('本地更新事件数量:', this.recordDetail.statistics.total_events);
        break;
    }

    // 强制触发Vue响应式更新
    this.$forceUpdate();
  }

  // 处理多进度条更新（直接设置为当前值，非累加）
  handleMultiAccumulated(data: any) {
    if (!data.process_accumulated) return

    const processAccumulated = data.process_accumulated
    console.log('收到多进度条更新:', processAccumulated)

    // 遍历 process_accumulated 对象，直接设置对应的进度值
    for (const [name, value] of Object.entries(processAccumulated)) {
      if (this.processValues[name] === undefined) {
        // 如果之前没有值，初始化
        this.$set(this.processValues, name, value as number)
      } else {
        // 直接替换为新值（不是累加）
        this.processValues[name] = value as number
      }
      console.log(`进度 ${name} 设置为: ${this.processValues[name]}`)
    }
  }
}
</script>

<style scoped>
.chat-layout {
  display: flex;
  max-width: 1400px;
  margin: auto;
  gap: 10px;
}
.chapter-list {
  width: 200px;
  padding-right: 10px;
  border-right: 1px solid #ccc;
}
.chat-main {
  flex: 1;
  padding-left: 10px;
}
.chat-container {
  padding: 20px;
  max-width: 600px;
  margin: auto;
}
.chat-history {
  min-height: 300px;
  max-height: 500px;
  border: 1px solid #ddd;
  padding: 10px;
  margin-bottom: 10px;
  background-color: #f9f9f9;
  overflow-y: auto;
}
.chat-message {
  display: flex;
  margin-bottom: 10px;
  align-items: flex-start;
}
.chat-message.left {
  flex-direction: row;
}
.chat-message.right {
  flex-direction: row-reverse;
}
.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin: 0 10px;
}
.bubble {
  max-width: 70%;
  padding: 10px;
  border-radius: 10px;
  background-color: #e6f7ff;
  word-break: break-word;
  text-align: left;
  position: relative;
}
.chat-message.right .bubble {
  background-color: #d6f5d6;
  text-align: left;
}
.sender {
  font-weight: bold;
  display: block;
}
.chat-input {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
}

.loading-hint {
  color: #999;
  font-size: 14px;
}

.input-controls {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.rag-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.rag-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.asr-hint {
  color: #999;
  font-size: 12px;
  padding: 5px 0;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 章节列表样式增强 */
.chapter-item {
  margin-bottom: 8px;
}

.chapter-item.active .el-button {
  background: linear-gradient(to right, #409EFF 90%, transparent 10%);
  color: white;
  border-radius: 4px 0 0 4px;
}

.chapter-item .el-button {
  width: 100%;
  text-align: left;
}

.play-button {
  position: absolute;
  right: 5px;
  top: 5px;
  padding: 2px 4px;
  font-size: 12px;
  opacity: 0.7;
  transition: opacity 0.3s;
}

.play-button:hover {
  opacity: 1;
}

/* 侧边栏样式 */
.sidebar {
  width: 250px;
  padding-left: 10px;
  border-left: 1px solid #ccc;
}

.user-stats {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stats-card {
  margin-bottom: 0;
}

.stats-card.clickable {
  cursor: pointer;
  transition: transform 0.2s;
}

.stats-card.clickable:hover {
  transform: translateY(-2px);
}

.stats-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  color: #409EFF;
}

.affection-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.affection-item:last-child {
  border-bottom: none;
}

.character-name {
  font-weight: 500;
  color: #333;
}

.affection-value {
  font-weight: bold;
  color: #409EFF;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}

.stat-value {
  font-weight: bold;
  color: #67C23A;
}

.empty-state {
  text-align: center;
  color: #999;
  padding: 20px 0;
  font-size: 14px;
}

/* 弹窗样式 */
.detail-item {
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 12px;
  background-color: #fafafa;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.detail-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
}

.detail-info {
  flex: 1;
}

.detail-content h4 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 16px;
  font-weight: bold;
}

.detail-info h4 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 16px;
  font-weight: bold;
}

.affection-detail,
.item-detail,
.achievement-detail,
.egg-detail,
.event-detail,
.time-info {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.label {
  font-weight: 500;
  color: #666;
  margin-right: 8px;
  min-width: 80px;
}

.value {
  color: #333;
}

.affection-value {
  font-weight: bold;
  color: #409EFF;
  font-size: 18px;
}

.item-quantity {
  font-weight: bold;
  color: #67C23A;
}

.time-info {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
}

.empty-detail {
  text-align: center;
  color: #999;
  padding: 40px 0;
  font-size: 16px;
}

/* 进度条样式 */
.progress-container {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.progress-card {
  padding: 12px 16px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.progress-title {
  font-weight: bold;
  font-size: 16px;
  color: #333;
}

.progress-text {
  font-weight: bold;
  font-size: 14px;
  color: #409EFF;
}

.progress-bar {
  margin-bottom: 10px;
}

.progress-markers {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.marker-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.marker-value {
  font-size: 14px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: #f5f5f5;
}

.start-marker .marker-value {
  color: #67C23A;
  background-color: #f0f9ff;
  border: 1px solid #67C23A;
}

.current-marker .marker-value {
  color: #409EFF;
  background-color: #e1f5fe;
  border: 1px solid #409EFF;
}

.end-marker .marker-value {
  color: #E6A23C;
  background-color: #fff8e1;
  border: 1px solid #E6A23C;
}
</style>